import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
} from 'antd';
import { ClockCircleOutlined, FilePdfOutlined, QrcodeOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { CATEGORIES, CATEGORY_COLORS, STATUSES, STATUS_COLORS, date, errorText, money } from './format';

/**
 * One return: its data, the statement lines that paid it and every change a
 * person made. Accountants can type in what a picture-only notice holds and
 * overrule the status, always with a reason.
 */
export default function ReturnDrawer({ company, id, projects, canManage, hasStatements = true, onSlip, onClose, onChanged }) {
  const { t } = useTranslation('taxReturns');
  const [editForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [editing, setEditing] = useState(false);

  const { data: r, refetch } = useQuery({
    queryKey: ['tax-return', company, id],
    queryFn: () => API.taxReturns.getTaxReturn(company, id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!r) return;
    setEditing(r.entry === 'pending' && canManage);
    editForm.setFieldsValue({
      bop: r.bop,
      amount: r.amount,
      dueDate: r.dueDate ? dayjs(r.dueDate) : null,
      purpose: r.purpose,
      title: r.title,
      category: r.category || null,
      project: r.project?._id || null,
      reason: '',
    });
    statusForm.setFieldsValue({ status: r.override?.status || 'auto', reason: '' });
  }, [r, canManage, editForm, statusForm]);

  const done = () => {
    refetch();
    onChanged?.();
  };

  const save = useMutation({
    mutationFn: (values) =>
      API.taxReturns.updateTaxReturn(company, id, {
        ...values,
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : undefined,
        // A cleared select gives undefined, which the server would read as „unchanged".
        category: values.category ?? null,
      }),
    onSuccess: () => {
      setEditing(false);
      done();
    },
  });

  const override = useMutation({
    mutationFn: ({ status, reason }) =>
      API.taxReturns.overrideTaxReturnStatus(company, id, {
        status: status === 'auto' ? null : status,
        reason,
      }),
    onSuccess: done,
  });

  // The window is opened inside the click, before the link arrives: opened after an await it is a blocked popup.
  const openFile = async (which = 'return') => {
    const win = window.open('', '_blank');
    try {
      const url = await API.taxReturns.getTaxReturnFileUrl(company, id, which);
      if (win) {
        win.opener = null;
        win.location.href = url;
      }
    } catch {
      win?.close();
    }
  };

  const statusTag = (s) =>
    s ? <Tag color={STATUS_COLORS[s]}>{t(`status.${s}`, { context: canManage ? undefined : 'client' })}</Tag> : null;
  const historyValue = (v) =>
    v && typeof v === 'object'
      ? Object.entries(v)
          .map(([k, x]) => `${t(`fields.${k}`, { defaultValue: k })}: ${x ?? '—'}`)
          .join(', ')
      : String(v ?? '—').replace(/^auto:(.*)$/, (_, s) => `${t('override.auto')} (${t(`status.${s}`)})`).replace(
          new RegExp(`^(${STATUSES.join('|')})$`),
          (s) => t(`status.${s}`)
        );

  return (
    <Drawer open={Boolean(id)} onClose={onClose} width={760} title={r ? r.title || r.bop : ''} destroyOnClose>
      {r && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {r.entry === 'pending' && (
            <Alert
              type={canManage ? 'warning' : 'info'}
              showIcon
              message={t('drawer.pendingHelp', { context: canManage ? undefined : 'client' })}
            />
          )}
          {r.dueInDays !== null && r.dueInDays !== undefined && (
            <Alert
              type="warning"
              showIcon
              icon={<ClockCircleOutlined />}
              message={r.dueInDays === 0 ? t('dueSoon.drawerToday') : t('dueSoon.drawer', { count: r.dueInDays })}
            />
          )}
          {!canManage && r.status === 'no_statement' && <Alert type="info" showIcon message={t('drawer.noStatementClient')} />}
          {r.warnings?.length > 0 && <Alert type="warning" showIcon message={r.warnings.join('; ')} />}

          <Descriptions size="small" column={2} bordered>
            {hasStatements && (
            <Descriptions.Item label={t('fields.status')} span={2}>
              <Space>
                {statusTag(r.status)}
                {r.override && (
                  <span className="text-xs opacity-70">
                    {t('drawer.overridden', { auto: t(`status.${r.autoStatus}`) })}
                  </span>
                )}
              </Space>
            </Descriptions.Item>
            )}
            <Descriptions.Item label={t('fields.project')}>
              {r.project ? `${r.project.code} ${r.project.name}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.type')}>{r.type}</Descriptions.Item>
            <Descriptions.Item label={t('fields.category')} span={2}>
              {r.category ? (
                <Tag color={CATEGORY_COLORS[r.category]}>{t(`category.${r.category}`)}</Tag>
              ) : (
                <span className="opacity-60">{t('filters.noCategory')}</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.bop')}>{r.bop || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('fields.returnNumber')}>{r.returnNumber || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('fields.purpose')}>
              {r.purpose || (r.period?.month ? `${r.period.month}/${r.period.year}` : '—')}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.filedAt')}>{date(r.filedAt)}</Descriptions.Item>
            <Descriptions.Item label={t('fields.dueDate')}>{date(r.dueDate)}</Descriptions.Item>
            <Descriptions.Item label={t('fields.paymentAccount')}>{r.paymentAccount || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('fields.amount')}>{money(r.amount)}</Descriptions.Item>
            {r.type === 'PDPO' && (
              <>
                <Descriptions.Item label={t('fields.tax')}>{money(r.tax)}</Descriptions.Item>
                <Descriptions.Item label={t('fields.interest')}>{money(r.interest)}</Descriptions.Item>
                <Descriptions.Item label={t('fields.incomeDate')}>{date(r.incomeDate)}</Descriptions.Item>
              </>
            )}
            {hasStatements && (
              <Descriptions.Item label={t('fields.paidAmount')}>{money(r.paidAmount)}</Descriptions.Item>
            )}
            <Descriptions.Item label={t('fields.file')} span={2}>
              <Space>
                <span className="text-xs">{r.source?.sourcePath || r.fileName}</span>
                {r.hasFile && (
                  <Button shape="round" size="small" icon={<FilePdfOutlined />} onClick={() => openFile('return')}>
                    {/\.xml$/i.test(r.fileName || '') ? t('drawer.openXml') : t('drawer.openFile')}
                  </Button>
                )}
                {r.hasOrderFile && (
                  <Button shape="round" size="small" icon={<FilePdfOutlined />} onClick={() => openFile('order')}>
                    {t('drawer.openOrder')}
                  </Button>
                )}
              </Space>
            </Descriptions.Item>
            {r.abExport && (
              <Descriptions.Item label={t('ab.exportedLabel')} span={2}>
                {t('ab.exportedTooltip', { date: date(r.abExport.at), by: r.abExport.byName || '—' })}
                {' · '}
                {t('ab.folder')} {r.abExport.folder}, {date(r.abExport.date)}
              </Descriptions.Item>
            )}
          </Descriptions>

          {r.type === 'PDPO' && r.recipients?.length > 0 && (
            <>
              <Divider orientation="left" plain>
                {t('drawer.recipients')}
              </Divider>
              <Table
                size="small"
                rowKey={(x, i) => `${x.name}-${i}`}
                pagination={false}
                dataSource={r.recipients}
                columns={[
                  { title: t('recipients.name'), dataIndex: 'name' },
                  { title: t('recipients.country'), dataIndex: 'country', width: 80 },
                  { title: t('recipients.incomeCode'), dataIndex: 'incomeCode', width: 90 },
                  { title: t('recipients.gross'), dataIndex: 'gross', align: 'right', render: money },
                  { title: t('recipients.rate'), dataIndex: 'rate', width: 70, align: 'right', render: (v) => (v ?? '—') + (v != null ? '%' : '') },
                  { title: t('fields.tax'), dataIndex: 'tax', align: 'right', render: money },
                ]}
              />
            </>
          )}

          {r.paymentSlip && onSlip && (
            <Button shape="round" icon={<QrcodeOutlined />} onClick={() => onSlip(r)}>
              {t('slip.button')}
            </Button>
          )}

          {hasStatements && (
          <>
          <Divider orientation="left" plain>
            {t('drawer.payments')}
          </Divider>
          {r.paymentLines.length ? (
            <Table
              size="small"
              rowKey="_id"
              pagination={false}
              dataSource={r.paymentLines}
              columns={[
                { title: t('fields.bookingDate'), dataIndex: 'bookingDate', render: date, width: 100 },
                { title: t('fields.fromAccount'), dataIndex: 'account', width: 180 },
                {
                  title: t('fields.statement'),
                  dataIndex: 'statement',
                  render: (s) => (s ? `${s.number} / ${date(s.date)}` : '—'),
                },
                { title: t('fields.purpose'), dataIndex: 'purpose' },
                { title: t('fields.amount'), dataIndex: 'amount', align: 'right', render: (v) => money(Math.abs(v)) },
              ]}
            />
          ) : (
            <p className="text-sm opacity-70">{t('drawer.noPayments')}</p>
          )}
          </>
          )}

          {canManage && (
            <>
              <Divider orientation="left" plain>
                {t('drawer.edit')}
              </Divider>
              {!editing ? (
                <Button shape="round" onClick={() => setEditing(true)}>{t('drawer.editButton')}</Button>
              ) : (
                <Form form={editForm} layout="vertical" onFinish={(v) => save.mutate(v)}>
                  <Space wrap align="start">
                    <Form.Item name="bop" label={t('fields.bop')} rules={[{ required: true }]}>
                      <Input style={{ width: 220 }} maxLength={25} />
                    </Form.Item>
                    <Form.Item name="amount" label={t('fields.amount')} rules={[{ required: true }]}>
                      <InputNumber style={{ width: 170 }} min={0} step={0.01} decimalSeparator="," />
                    </Form.Item>
                    <Form.Item name="dueDate" label={t('fields.dueDate')} rules={[{ required: true }]}>
                      <DatePicker format="DD.MM.YYYY" />
                    </Form.Item>
                    <Form.Item name="purpose" label={t('fields.purpose')}>
                      <Input style={{ width: 130 }} placeholder="PID-8-2026" />
                    </Form.Item>
                  </Space>
                  <Space wrap align="start">
                    <Form.Item name="project" label={t('fields.project')}>
                      <Select
                        allowClear
                        style={{ width: 240 }}
                        options={projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` }))}
                      />
                    </Form.Item>
                    <Form.Item name="title" label={t('fields.title')}>
                      <Input style={{ width: 300 }} />
                    </Form.Item>
                    <Form.Item name="category" label={t('fields.category')}>
                      <Select
                        allowClear
                        style={{ width: 200 }}
                        placeholder={t('filters.noCategory')}
                        options={CATEGORIES.map((c) => ({ value: c, label: t(`category.${c}`) }))}
                      />
                    </Form.Item>
                  </Space>
                  <Form.Item name="reason" label={t('fields.reasonOptional')}>
                    <Input />
                  </Form.Item>
                  {save.isError && <Alert type="error" showIcon message={errorText(t, save.error)} />}
                  <Space style={{ marginTop: 8 }}>
                    <Button shape="round" type="primary" htmlType="submit" loading={save.isPending}>
                      {t('drawer.save')}
                    </Button>
                    {r.entry !== 'pending' && <Button shape="round" onClick={() => setEditing(false)}>{t('drawer.cancel')}</Button>}
                  </Space>
                </Form>
              )}

              <Divider orientation="left" plain>
                {t('override.title')}
              </Divider>
              <Form form={statusForm} layout="vertical" onFinish={(v) => override.mutate(v)}>
                <Space wrap align="start">
                  <Form.Item name="status" label={t('fields.status')}>
                    <Select
                      style={{ width: 220 }}
                      options={[
                        { value: 'auto', label: t('override.auto') },
                        ...STATUSES.filter((s) => s !== 'pending_entry').map((s) => ({
                          value: s,
                          label: t(`status.${s}`),
                        })),
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    name="reason"
                    label={t('fields.reason')}
                    rules={[{ required: true, min: 3, message: t('errors.reason_required') }]}
                  >
                    <Input style={{ width: 380 }} />
                  </Form.Item>
                </Space>
                {override.isError && <Alert type="error" showIcon message={errorText(t, override.error)} />}
                <Button shape="round" htmlType="submit" loading={override.isPending}>
                  {t('override.submit')}
                </Button>
              </Form>
            </>
          )}

          {r.history?.length > 0 && (
            <>
              <Divider orientation="left" plain>
                {t('drawer.history')}
              </Divider>
              <Timeline
                items={r.history.map((h) => ({
                  children: (
                    <div className="text-sm">
                      <div>
                        <b>{date(h.at)} {dayjs(h.at).format('HH:mm')}</b> · {h.byName || '—'} ·{' '}
                        {t(`history.${h.action}`)}
                      </div>
                      <div>
                        {historyValue(h.from)} → {historyValue(h.to)}
                      </div>
                      {h.reason && <div className="opacity-70">{h.reason}</div>}
                    </div>
                  ),
                }))}
              />
            </>
          )}
        </Space>
      )}
    </Drawer>
  );
}
